/* ──────────────────────────────────────────────────────────────────────
 * FollowUpManagementCard  —  Section 3 of the redesigned Lead Actions
 *
 * Premium amber-highlighted card that surfaces the lead's pending and
 * overdue follow-up tasks above everything else operationally. Replaces
 * the prior "Upcoming Follow-ups" card with a more visually weighted
 * design — amber rail + warning iconography so the rep can't miss when
 * a task is due today / overdue.
 *
 *   • Pending tasks list (oldest-due-first)
 *   • Per-row: Mark Completed · Reschedule · Cancel
 *   • Inline reschedule picker
 *   • Recently-completed list (last 5) collapsed at the bottom
 *
 * Creation is handled by QuickActionsCard → "Schedule Follow-up" so this
 * card stays focused on review/management of EXISTING follow-ups.
 *
 * Backend wiring (no schema changes):
 *   GET  /tasks?lead_id=:id   (existing fetchLeadTasks)
 *   PUT  /tasks/:id           (status: completed / cancelled / due_at)
 * ──────────────────────────────────────────────────────────────────── */

import React, { useState } from "react";
import {
  Box, Typography, Stack, Chip, IconButton, Tooltip, Button, Alert,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PremiumDateTimePicker from "../../Components/PremiumDateTimePicker";
import {
  updateTask, type Task, PRIORITY_CONFIG as TASK_PRIORITY,
} from "../Tasks/taskApi";

const NAVY = "#1a3353";
const AMBER_LINE   = "#f59e0b";
const AMBER_BG     = "#fffbeb";
const AMBER_BORDER = "#fde68a";

interface Props {
  tasks: Task[];
  onChanged?: () => void;
  onOpenTask?: (task: Task) => void;
  /** Read-only historical mode — previous assignee. Per-row controls
   *  (complete / reschedule / cancel) hide, and the inline reschedule
   *  form is suppressed. The pending + completed lists stay visible so
   *  the audit trail reads cleanly. */
  readOnly?: boolean;
}

export default function FollowUpManagementCard({ tasks, onChanged, onOpenTask, readOnly = false }: Props) {
  const [error, setError]           = useState<string | null>(null);
  const [reschedId, setReschedId]   = useState<number | null>(null);
  const [reschedDate, setReschedDate] = useState("");

  const pending   = tasks.filter((t) => t.status === "pending" || t.status === "in_progress");
  const completed = tasks.filter((t) => t.status === "completed");
  const overdueCount = pending.filter((t) => new Date(t.due_at).getTime() < Date.now()).length;

  const handleComplete = async (taskId: number) => {
    setError(null);
    try {
      await updateTask(taskId, { status: "completed" } as Partial<Task>);
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "Couldn't complete follow-up");
    }
  };

  const handleCancel = async (taskId: number) => {
    setError(null);
    try {
      await updateTask(taskId, { status: "cancelled" } as Partial<Task>);
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "Couldn't cancel follow-up");
    }
  };

  const openReschedule = (task: Task) => {
    setReschedId(task.id);
    setReschedDate(task.due_at);
  };

  const submitReschedule = async () => {
    if (!reschedId || !reschedDate) return;
    setError(null);
    try {
      await updateTask(reschedId, { due_at: reschedDate } as Partial<Task>);
      setReschedId(null);
      setReschedDate("");
      onChanged?.();
    } catch (e: any) {
      setError(e?.message || "Couldn't reschedule");
    }
  };

  return (
    <Box>
      {/* ── Section header — amber accent ──────────────────────── */}
      <Stack direction="row" alignItems="flex-start" gap={1} mb={1.4}>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" gap={0.7}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
              Follow-up Management
            </Typography>
            {overdueCount > 0 && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.3,
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
                color: "#dc2626", bgcolor: "#fef2f2",
                border: "1px solid #fecaca", borderRadius: "5px",
                px: 0.55, py: 0.05, textTransform: "uppercase",
              }}>
                <WarningAmberIcon sx={{ fontSize: 11 }} />
                {overdueCount} overdue
              </Box>
            )}
          </Stack>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
            {pending.length === 0
              ? "No active follow-ups — schedule one from Quick Actions."
              : `${pending.length} pending · oldest first`}
          </Typography>
        </Box>
        <Chip
          label={pending.length}
          size="small"
          sx={{
            bgcolor: overdueCount > 0 ? "#fef2f2" : AMBER_BG,
            color:   overdueCount > 0 ? "#ef4444" : AMBER_LINE,
            fontWeight: 800, height: 22, fontSize: 11,
            border: `1px solid ${overdueCount > 0 ? "#fecaca" : AMBER_BORDER}`,
          }}
        />
      </Stack>

      {/* ── Empty state — single compact row, alert style ─────── */}
      {pending.length === 0 && (
        <Stack direction="row" alignItems="center" gap={1} sx={{
          py: 0.8, px: 1, borderRadius: 1.5,
          bgcolor: AMBER_BG, border: `1px solid ${AMBER_BORDER}`,
        }}>
          <ScheduleIcon sx={{ fontSize: 15, color: AMBER_LINE, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#92400e", lineHeight: 1.3 }}>
            No active follow-ups
          </Typography>
        </Stack>
      )}

      {/* ── Pending tasks list ──────────────────────────────────── */}
      {pending.length > 0 && (
        <Stack gap={1}>
          {pending.map((task) => {
            const isOverdue    = new Date(task.due_at).getTime() < Date.now();
            const isResched    = reschedId === task.id;
            const pri          = TASK_PRIORITY[task.priority] || TASK_PRIORITY.medium;
            const assignee     = (task as any).assignee;
            const assigneeName = assignee
              ? (assignee.display_name
                  || [assignee.first_name, assignee.last_name].filter(Boolean).join(" ")
                  || assignee.username)
              : null;
            return (
              <Box key={task.id} sx={{
                borderRadius: 1.5,
                bgcolor: isOverdue ? "#fef2f2" : AMBER_BG,
                border: `1px solid ${isOverdue ? "#fecaca" : AMBER_BORDER}`,
                borderLeft: `3px solid ${isOverdue ? "#dc2626" : AMBER_LINE}`,
                p: 0.9,
              }}>
                <Stack direction="row" alignItems="flex-start" gap={0.9}>
                  {!readOnly && (
                    <Tooltip title="Mark completed">
                      <IconButton size="small" onClick={() => handleComplete(task.id)}
                        sx={{
                          border: "2px solid #d1d5db", width: 20, height: 20, mt: 0.2, p: 0,
                          "&:hover": { borderColor: "#10b981" },
                        }}>
                        <CheckCircleIcon sx={{ fontSize: 12, color: "#d1d5db" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box flex={1} minWidth={0}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY }} noWrap>
                      {task.title}
                    </Typography>
                    <Stack direction="row" gap={0.4} alignItems="center" mt={0.1} flexWrap="wrap">
                      <AccessTimeIcon sx={{ fontSize: 10.5, color: isOverdue ? "#ef4444" : "#92400e" }} />
                      <Typography sx={{
                        fontSize: 10,
                        color: isOverdue ? "#ef4444" : "#92400e",
                        fontWeight: isOverdue ? 800 : 700,
                      }}>
                        {new Date(task.due_at).toLocaleString("en-US", {
                          month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit", hour12: true,
                        })}
                        {isOverdue && " · OVERDUE"}
                      </Typography>
                    </Stack>
                    {assigneeName && (
                      <Typography sx={{ fontSize: 10, color: "#64748b", mt: 0.15 }} noWrap>
                        Assigned to <Box component="span" sx={{ fontWeight: 700, color: NAVY }}>{assigneeName}</Box>
                      </Typography>
                    )}
                    {task.notes && (
                      <Typography sx={{ fontSize: 10, color: "#64748b", mt: 0.15, lineHeight: 1.35 }}>
                        {task.notes.length > 70 ? task.notes.slice(0, 70) + "…" : task.notes}
                      </Typography>
                    )}
                    <Stack direction="row" gap={0.4} alignItems="center" mt={0.5}>
                      <Chip
                        label={pri.label} size="small"
                        sx={{ height: 17, fontSize: 9, bgcolor: `${pri.color}18`, color: pri.color, fontWeight: 700 }}
                      />
                      <Chip
                        label={task.status === "in_progress" ? "In progress" : "Pending"}
                        size="small"
                        sx={{ height: 17, fontSize: 9, bgcolor: "#e2e8f0", color: "#475569", fontWeight: 600 }}
                      />
                      <Box flex={1} />
                      {onOpenTask && (
                        <Tooltip title="Open task">
                          <IconButton size="small" onClick={() => onOpenTask(task)} sx={{ p: 0.2, color: "#64748b", "&:hover": { color: NAVY } }}>
                            <OpenInFullIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && (
                        <Tooltip title="Reschedule">
                          <IconButton size="small" onClick={() => openReschedule(task)}
                            sx={{ p: 0.2, color: "#64748b", "&:hover": { color: "#3b82f6" } }}>
                            <ScheduleIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!readOnly && (
                        <Tooltip title="Cancel follow-up">
                          <IconButton size="small" onClick={() => handleCancel(task.id)}
                            sx={{ p: 0.2, color: "#64748b", "&:hover": { color: "#ef4444" } }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {!readOnly && isResched && (
                      <Box mt={1} p={1} sx={{ bgcolor: "#fff", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                        <PremiumDateTimePicker
                          label="New date & time"
                          value={reschedDate}
                          onChange={(v) => setReschedDate(v)}
                        />
                        <Stack direction="row" gap={0.7} mt={0.8} justifyContent="flex-end">
                          <Button size="small" onClick={() => { setReschedId(null); setReschedDate(""); }} sx={{ textTransform: "none", fontSize: 11 }}>
                            Cancel
                          </Button>
                          <Button
                            size="small" variant="contained"
                            disabled={!reschedDate}
                            onClick={submitReschedule}
                            sx={{
                              textTransform: "none", fontSize: 11, bgcolor: NAVY,
                              "&:hover": { bgcolor: "#1e3a5f" },
                            }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* ── Recently completed (collapsed list) ─────────────────── */}
      {completed.length > 0 && (
        <Box mt={1.5}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, textTransform: "uppercase", mb: 0.5 }}>
            Recently completed
          </Typography>
          <Stack gap={0.4}>
            {completed.slice(0, 5).map((task) => (
              <Stack key={task.id} direction="row" alignItems="center" gap={0.8}>
                <CheckCircleIcon sx={{ fontSize: 14, color: "#10b981" }} />
                <Typography sx={{ fontSize: 11, color: "#94a3b8", textDecoration: "line-through" }} noWrap>
                  {task.title}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.2, fontSize: 11.5, py: 0.3, borderRadius: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
